import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ApolloError, useApolloClient } from '@apollo/client';
import { Modal, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    DeleteModuleDocument,
    type DeleteModuleMutation,
    type DeleteModuleMutationVariables,
} from '../../../api/mutations/deleteModule';

const useDeleteModule = (moduleId: string) => {
    const { t } = useTranslation(['moduleDetails']);

    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    return useCallback(() => {
        Modal.confirm({
            title: t('moduleDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('moduleDetails:deleteModal.content'),
            okText: t('moduleDetails:deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('moduleDetails:deleteModal.cancelButton'),
            onOk: async () => {
                try {
                    // loading message
                    message.loading({
                        content: t('moduleDetails:deleteModal.messages.submitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteModuleMutation, DeleteModuleMutationVariables>({
                            mutation: DeleteModuleDocument,
                            variables: { moduleId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('moduleDetails:deleteModal.messages.success'),
                        key: 'primary',
                    });

                    navigate('/admin/system/modules');
                } catch (error) {
                    if (error instanceof ApolloError) {
                        message.error(error.graphQLErrors[0].message);
                    }
                }
            },
        });
    }, [t, apolloClient, moduleId, navigate]);
};

export default useDeleteModule;
